import { useState, useEffect } from 'react'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/Checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  fetchMCPServersThunk,
  assignMCPServersToAdvisorThunk,
} from '@/redux/asyncThunks/mcpServer'
import {
  fetchRemoteMCPServersThunk,
  fetchRemoteMCPConnectionsThunk,
} from '@/redux/asyncThunks/remoteMCP'
import { selectRemoteMCPServers } from '@/redux/slices/remoteMCPSlice'
import { fetchAdvisorData } from '@/api/configuration'
import { MCPServer } from '@/api/mcpServer'
import { RemoteMCPServer, fetchRoomOwnerMCPConnections } from '@/api/remoteMCP'
import { FlaskConical, AlertCircle } from 'lucide-react'

interface MCPConfigProps {
  chatbotId: string
  roomId: string
  disabled?: boolean
}

interface DisplayServer {
  id: number
  name: string
  description: string | null | undefined
  enabled: boolean
  isRemote: boolean
  command?: string
  args?: string[]
  slug?: string
  connectionRequired?: boolean
  isConnected?: boolean
}

export default function MCPConfig({
  disabled,
  chatbotId,
  roomId,
}: MCPConfigProps) {
  const dispatch = useAppDispatch()
  const { servers: roomServers, loading: serversLoading } = useAppSelector(
    (state) => state.mcpServer
  )
  const remoteServers = useAppSelector(selectRemoteMCPServers)

  const [selectedServerIds, setSelectedServerIds] = useState<number[]>([])
  const [initialServerIds, setInitialServerIds] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [ownerConnectedIds, setOwnerConnectedIds] = useState<Set<number>>(
    new Set()
  )

  useEffect(() => {
    const loadData = async () => {
      if (!chatbotId || !roomId) return
      try {
        setIsLoading(true)
        const [, , , advisorResponse, ownerConnections] = await Promise.all([
          dispatch(fetchMCPServersThunk(roomId)),
          dispatch(fetchRemoteMCPServersThunk()),
          dispatch(fetchRemoteMCPConnectionsThunk()),
          fetchAdvisorData(chatbotId),
          fetchRoomOwnerMCPConnections(roomId).catch(() => ({
            connected_server_ids: [] as number[],
          })),
        ])
        const assignedIds = (advisorResponse.data.mcp_servers || []).map(
          (s: any) => s.id
        )
        setSelectedServerIds(assignedIds)
        setInitialServerIds(assignedIds)
        setOwnerConnectedIds(new Set(ownerConnections.connected_server_ids))
      } catch {
        toast.error('Failed to load MCP configuration')
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [chatbotId, roomId, dispatch])

  const handleToggleServer = (serverId: number) => {
    if (disabled) return
    setSelectedServerIds((prev) =>
      prev.includes(serverId)
        ? prev.filter((id) => id !== serverId)
        : [...prev, serverId]
    )
  }

  const handleSave = async () => {
    if (disabled || isSaving) return
    setIsSaving(true)
    try {
      await dispatch(
        assignMCPServersToAdvisorThunk({ chatbotId, serverIds: selectedServerIds })
      ).unwrap()
      setInitialServerIds(selectedServerIds)
      toast.success('MCP server assignments saved successfully')
    } catch {
      toast.error('Failed to save MCP server assignments')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => setSelectedServerIds(initialServerIds)

  const hasChanges =
    JSON.stringify([...selectedServerIds].sort()) !==
    JSON.stringify([...initialServerIds].sort())
  const allServers: DisplayServer[] = [
    ...roomServers
      .filter((s: MCPServer) => s.enabled)
      .map((s: MCPServer) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        enabled: s.enabled,
        isRemote: false,
        command: s.command,
        args: s.args,
      })),
    ...remoteServers
      .filter((s: RemoteMCPServer) => s.enabled)
      .map((s: RemoteMCPServer) => ({
        id: s.id,
        name: s.name,
        description: s.description,
        enabled: s.enabled,
        isRemote: true,
        slug: s.slug,
        connectionRequired: true,
        isConnected: ownerConnectedIds.has(s.id),
      })),
  ]

  return (
    <div className='space-y-6'>
      <h2 className='text-2xl font-semibold text-foreground'>
        MCP Server Assignment
      </h2>

      <Card className={disabled ? 'opacity-50' : ''}>
        <CardContent className='py-6'>
          {isLoading || serversLoading ? (
            <p className='text-sm text-muted-foreground'>
              Loading MCP servers...
            </p>
          ) : allServers.length === 0 ? (
            <div className='py-4 text-center'>
              <p className='mb-2 text-sm text-muted-foreground'>
                No MCP servers available.
              </p>
              <p className='text-xs text-muted-foreground'>
                Configure MCP servers in room settings, or connect a research
                account (e.g. Scite.ai) in Advisor Settings.
              </p>
            </div>
          ) : (
            <div className='space-y-4'>
              <div>
                <Label className='block mb-3 text-sm font-medium'>
                  Available MCP Servers
                </Label>
                <p className='mb-4 text-xs text-muted-foreground'>
                  Select which MCP servers this advisor can use. Multiple
                  servers can be selected.
                </p>
              </div>

              <div className='space-y-3'>
                {allServers.map((server) => {
                  const isChecked = selectedServerIds.includes(server.id)
                  const notConnected = server.connectionRequired && !server.isConnected

                  return (
                    <div
                      key={server.id}
                      className={cn(
                        'flex items-start p-3 space-x-3 rounded-lg border',
                        disabled || notConnected
                          ? 'cursor-not-allowed opacity-60'
                          : 'hover:bg-accent'
                      )}
                    >
                      <Checkbox
                        id={`server-${server.id}`}
                        checked={isChecked}
                        onChange={() => !notConnected && handleToggleServer(server.id)}
                        disabled={disabled || notConnected}
                      />
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <Label
                            htmlFor={`server-${server.id}`}
                            className={cn(
                              'font-medium',
                              !notConnected && 'cursor-pointer'
                            )}
                          >
                            {server.name}
                          </Label>
                          {server.isRemote && (
                            <Badge
                              variant='outline'
                              className='text-xs gap-1 py-0'
                            >
                              <FlaskConical className='h-2.5 w-2.5' />
                              Research
                            </Badge>
                          )}
                          {server.isConnected && (
                            <Badge
                              variant='outline'
                              className='text-xs text-green-600 border-green-300 bg-green-50 py-0'
                            >
                              Connected
                            </Badge>
                          )}
                        </div>

                        {server.description && (
                          <p className='mt-1 text-xs text-muted-foreground'>
                            {server.description}
                          </p>
                        )}

                        {!server.isRemote && server.command && (
                          <p className='mt-1 text-xs text-muted-foreground font-mono'>
                            {server.command}{' '}
                            {server.args?.slice(0, 2).join(' ')}
                            {(server.args?.length ?? 0) > 2 && '…'}
                          </p>
                        )}

                        {notConnected && (
                          <p className='mt-1 flex items-center gap-1 text-xs text-amber-600'>
                            <AlertCircle className='h-3 w-3' />
                            Room owner hasn&apos;t connected this service yet
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {hasChanges && (
                <div className='flex gap-2 justify-end pt-4 border-t'>
                  <Button
                    variant='outline'
                    onClick={handleReset}
                    disabled={disabled || isSaving}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={disabled || isSaving}
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}

              {selectedServerIds.length > 0 && !hasChanges && (
                <div className='p-3 rounded-lg border border-green-200 bg-green-50/50'>
                  <p className='text-sm text-green-900'>
                    {selectedServerIds.length} MCP server
                    {selectedServerIds.length !== 1 ? 's' : ''} assigned
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
